// SUR-R63, SUR-R66, SUR-R78. The contest's tiebreak menu is exactly three
// options — splitMedal, noWinner, commissionerPick — chosen once at contest
// setup (CONTEST_TIEBREAKERS, src/domain/contestConfig.ts). The identical
// setting resolves both a contest medal tie and, only when points also tie, a
// season trophy tie (SUR-R46, SUR-R66): there is no second, separate tiebreak
// resolution path for the trophy.
//
// tiedCandidateIds and winnerIds are bare `string`, not typeEntryId or
// typeMemberId, precisely so one call resolves an entry-level medal tie and a
// member-level trophy tie with no cast. Both are documentary aliases over
// string, and this is the one call site that needs either shape.
//
// Two different questions live in this file.
//
// 1. Who won, once a genuine tie has been handed over: resolveTiebreak().
//    splitMedal names every tied candidate, noWinner names nobody, and
//    commissionerPick reports awaiting until at least one winner has been
//    named, then reports the currently named set. Whether the contest is over
//    and whether a tie genuinely exists is decided upstream, never here.
//
// 2. May a named set still be changed: resolveTiebreakEdit(). Only a
//    commissionerPick contest has a named set to change at all, so any other
//    tiebreaker refuses outright. SUR-R78 then keeps a Commissioner's named
//    winners editable for 48 hours from the first time they were named, then
//    they are set. Q166, answered 2026-08-16 as option A, settles the
//    collision with SUR-R61's "a confirmed contest never changes": confirming
//    the contest ends that window early, so whatever names sit on the tie at
//    the instant of confirmation are final, however many hours were left.
//
// Both functions are pure. Every instant arrives as a caller-supplied epoch
// millisecond, and nothing here reads a clock, a database, or the network.
// Neither function validates its input: a call with a single candidate and no
// real tie is a caller error, not a case guarded against here.

import type { typeContestTiebreaker, typeEpochMs } from "../domain";

export type typeResolveTiebreakInput = {
  tiebreaker: typeContestTiebreaker;
  tiedCandidateIds: readonly string[];
  commissionerNamedWinnerIds: readonly string[] | null;
  commissionerNamedAtMs: typeEpochMs | null;
  nowMs: typeEpochMs;
};

export type typeResolveTiebreakResult =
  | { outcome: "resolved"; winnerIds: readonly string[] }
  | { outcome: "awaitingCommissionerPick" };

/**
 * SUR-R78. Named tiebreak winners stay editable for 48 hours from the first
 * naming, then they are set.
 */
export const TIEBREAK_EDIT_WINDOW_MS = 48 * 60 * 60 * 1000;

export type typeTiebreakEditRefusalReason =
  "tiebreakerNotCommissionerPick" | "alreadyConfirmed" | "tiebreakWindowClosed";

export type typeResolveTiebreakEditInput = {
  tiebreaker: typeContestTiebreaker;
  commissionerNamedAtMs: typeEpochMs | null;
  contestConfirmedAtMs: typeEpochMs | null;
  nowMs: typeEpochMs;
};

export type typeResolveTiebreakEditResult =
  | { decision: "accepted" }
  | { decision: "refused"; reason: typeTiebreakEditRefusalReason };

export function resolveTiebreak(
  input: typeResolveTiebreakInput,
): typeResolveTiebreakResult {
  if (input.tiebreaker === "splitMedal") {
    return { outcome: "resolved", winnerIds: input.tiedCandidateIds };
  }

  if (input.tiebreaker === "noWinner") {
    return { outcome: "resolved", winnerIds: [] };
  }

  // input.tiebreaker === "commissionerPick", the only remaining member of
  // typeContestTiebreaker (CONTEST_TIEBREAKERS has exactly three).
  if (
    input.commissionerNamedWinnerIds === null ||
    input.commissionerNamedWinnerIds.length === 0
  ) {
    return { outcome: "awaitingCommissionerPick" };
  }

  return { outcome: "resolved", winnerIds: input.commissionerNamedWinnerIds };
}

export function resolveTiebreakEdit(
  input: typeResolveTiebreakEditInput,
): typeResolveTiebreakEditResult {
  // SUR-R63. Only commissionerPick produces a named winner set, so under
  // splitMedal or noWinner there is nothing an edit could change.
  if (input.tiebreaker !== "commissionerPick") {
    return { decision: "refused", reason: "tiebreakerNotCommissionerPick" };
  }

  // Q166, SUR-R61. Confirm is the more final of the two actions and ends the
  // naming window early, whether or not 48 hours have passed.
  if (input.contestConfirmedAtMs !== null) {
    return { decision: "refused", reason: "alreadyConfirmed" };
  }

  // Nothing has been named yet, so no window is running: this is a first
  // naming, not a change to one.
  if (input.commissionerNamedAtMs === null) {
    return { decision: "accepted" };
  }

  // SUR-R78. Editable FOR 48 hours, then set, so the window is already closed
  // at exactly 48 hours rather than one millisecond after.
  if (input.nowMs - input.commissionerNamedAtMs >= TIEBREAK_EDIT_WINDOW_MS) {
    return { decision: "refused", reason: "tiebreakWindowClosed" };
  }

  return { decision: "accepted" };
}
